<script setup lang="ts">
import { Loader2, Search, Users } from '@lucide/vue'
import { useDebounceFn } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'

import { useImpersonation, type ImpersonationTarget } from '../composables/useImpersonation'

import * as impersonationRoutes from '@/wayfinder/routes/portal/impersonation'


const { t } = useI18n()
const { canImpersonate, isImpersonating, isLoading, start, initFromPage } = useImpersonation()

const open = ref(false)
const searchQuery = ref('')
const users = ref<ImpersonationTarget[]>([])
const loadingUsers = ref(false)

// Initialize from page props
onMounted(() => {
  initFromPage()
})

/**
 * Check if the picker should be visible
 */
const isVisible = computed(() => {
  return canImpersonate.value && !isImpersonating.value
})

/**
 * Fetch users from the server
 */
const fetchUsers = async (search: string) => {
  if (!canImpersonate.value) return

  loadingUsers.value = true

  try {
    const response = await fetch(
      impersonationRoutes.users.url({ query: { search, limit: 20 } }),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      }
    )

    if (!response.ok) {
      throw new Error('Failed to fetch users')
    }

    const data = await response.json()
    users.value = data.users ?? []
  } catch (error) {
    console.error('Failed to fetch users:', error)
    users.value = []
  } finally {
    loadingUsers.value = false
  }
}

/**
 * Debounced search
 */
const debouncedFetch = useDebounceFn((search: string) => {
  void fetchUsers(search)
}, 300)

/**
 * Watch search query changes
 */
watch(searchQuery, (value) => {
  void debouncedFetch(value)
})

/**
 * Fetch initial users when popover opens
 */
watch(open, (isOpen) => {
  if (isOpen && users.value.length === 0) {
    void fetchUsers('')
  }
})

/**
 * Handle user selection
 */
const handleSelect = async (user: ImpersonationTarget) => {
  try {
    await start(user.id, user.type)
    open.value = false
    toast.success(t('portal.impersonation.started'))
  } catch {
    toast.error(t('portal.impersonation.start_error'))
  }
}
</script>

<template>
  <div v-if="isVisible" class="px-2 py-1.5">
    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button
          variant="ghost"
          class="w-full justify-start gap-2 px-2 text-sm font-normal"
          :disabled="isLoading"
        >
          <Users class="h-4 w-4 opacity-60" aria-hidden="true" />
          <span>{{ t('portal.impersonation.menu_title') }}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-72 p-0" align="start" side="right">
        <div class="flex flex-col">
          <!-- Search Input -->
          <div class="relative border-b p-2">
            <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <Search class="h-4 w-4 text-muted-foreground" />
            </div>
            <Input
              v-model="searchQuery"
              :placeholder="t('portal.impersonation.search_placeholder')"
              class="h-9 pl-9"
            />
          </div>

          <!-- Results List -->
          <ScrollArea class="max-h-64 [&>[data-slot=scroll-area-viewport]]:max-h-64">
            <!-- Loading State -->
            <div
              v-if="loadingUsers"
              class="flex items-center justify-center py-6"
            >
              <Loader2 class="h-5 w-5 animate-spin text-muted-foreground" />
            </div>

            <!-- Empty State -->
            <div
              v-else-if="users.length === 0"
              class="py-6 text-center text-sm text-muted-foreground"
            >
              {{ t('portal.impersonation.no_users_found') }}
            </div>

            <!-- User List -->
            <div v-else class="p-1">
              <button
                v-for="user in users"
                :key="`${user.type}-${user.id}`"
                type="button"
                class="flex w-full cursor-pointer flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-accent focus:bg-accent focus:outline-none"
                @click="handleSelect(user)"
              >
                <span class="text-sm font-medium">{{ user.name ?? user.email }}</span>
                <span v-if="user.name && user.email" class="text-xs text-muted-foreground">
                  {{ user.email }}
                </span>
              </button>
            </div>
          </ScrollArea>
        </div>
      </PopoverContent>
    </Popover>
  </div>
</template>
